<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = withDefaults(defineProps<{ stoneLayout?: string, stoneTag?: string, show?: boolean }>(), {
  stoneLayout: "",
  stoneTag: "none",
  show: false
});
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonesAddons.vue, but reads the single, non-group-scoped
 * "stones.presets" variant idx via getVariants() instead of a per-stone-group idx.
 * The actual fetch (and thus the api call triggered by getVariants()/getItemVariants()
 * whenever a variant has no values loaded yet) only happens while props.show is true,
 * so the preset library is only loaded from the api once it is actually shown.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = `stones.stone_presets`

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

// whether the "more" values (list entry with the right-arrow) have been revealed inline
const showMore = ref(false)

// the current stone layout / stone tag combination presets are fetched for
const currentAddon = computed(() => `.${props.stoneLayout}.${props.stoneTag}`)

// Tracks which combination the cached "stones.stone_presets" variant values currently
// belong to. getItemVariants() only (re-)fetches a variant while its values are still
// empty, so simply changing props.stoneLayout/props.stoneTag would otherwise keep
// showing the previously loaded layout's presets forever. Whenever currentAddon actually
// changes, the cached values are cleared here (as a side effect, hence the watch instead
// of doing it inside the variants computed) to force a fresh reload.
const loadedPresetsAddon = ref<string | null>(null)

watch(() => props.stoneLayout, (stoneLayout) => {
  for (const sessionItemIdx in session.value.items) {
    store.commit("session/setVariantValues", {
      itemIdx: sessionItemIdx,
      variantIdx: variantIdx,
      values: []
    });
  }

}, {immediate: true})

const variants = computed(() => props.show ? parametersVariations.getVariants(currentAddon.value) : [])

/**
 * preset variants (transferred from itemV3StonesAddons.vue's "addon variants")
 */

const active = computed(() => variants.value[0])

// only keep the presets matching the requested stone layout / stone tag - if the
// preset data doesn't provide one of those fields it is not used to filter it out
const variantsList = computed(() => {
  const list = active.value?.values || []
  return list.filter((v: any) => {
    const matchesLayout = !v.stone_layout || v.stone_layout === props.stoneLayout
    const matchesTag = !v.stoneTag || v.stoneTag === props.stoneTag
    return matchesLayout && matchesTag
  })
})

// the current match should always be listed first, so it is always part of the
// directly visible values instead of potentially being hidden behind "more"
const orderedVariantsList = computed(() => {
  // no special order
  return variantsList.value
  /**
   * Old Version, matched preset is frist in row
   *
  const matchedIdx = variantsList.value.findIndex((v: any) => v.match)
  if (matchedIdx <= 0) {
    return variantsList.value
  }
  const matchedValue = variantsList.value[matchedIdx]
  return [
    matchedValue,
    ...variantsList.value.slice(0, matchedIdx),
    ...variantsList.value.slice(matchedIdx + 1)
  ]
      */
})

// all values are shown directly, the first 5 right away and the rest hidden behind the
// "more"/"less" toggle below
const visibleValues = computed(() => orderedVariantsList.value.slice(0, 3))

const moreValues = computed(() => orderedVariantsList.value.slice(3))

const hasMoreValues = computed(() => moreValues.value.length > 0)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

const getPresetImageUrl = (presetId: string, size: 300 | 600) => {
  return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_presets/${size}/${presetId}.webp`
}

</script>

<template>
  <div v-if="props.show && active" class="w-full">
    <ul class="flex gap-x-1 flex-wrap mb-2 w-full w-full justify-center ">
      <!-- stone presets list - visible values (first 5) -->
      <li v-for="(variantData, variantDataIdx) in visibleValues" :key="variantDataIdx"
          class="cursor-pointer flex flex-col items-center px-1 rounded-2xl hover:bg-[#f3f0e9]"
          @click="handleValuePatch(active.idx, variantData)"
          :class="{ 'bg-[#f3f0e9]': variantData.match }"
      >
        <div class="rounded-2xl bg-white flex flex-col items-center p-2"
             :class="{
               'border-2 border-brand-main': variantData.match,
               'border-2 border-transparent': !variantData.match
             }"
        >
          <img
              :src="getPresetImageUrl(variantData.idx, 300)"
              :srcset="getPresetImageUrl(variantData.idx, 600)"
              class="max-w-20 w-full h-auto object-contain rounded-2xl"
          >
          <span class="text-parameter-text text-[10px] overflow-hidden max-w-20 mx-auto text-center mt-1">
            {{ variantData.title }}
          </span>
        </div>
      </li>
      <!-- "more" trigger - placed as the last entry of the visible list, looks like another list item -->
      <li v-if="hasMoreValues && !showMore"
          class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = true"
      >
        <div class="h-14 w-14 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 -rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          Mehr
        </span>
      </li>
      <!-- stone presets list - remaining values (revealed inline once the "more" entry above was clicked) -->
      <template v-if="showMore">
        <li v-for="(variantData, variantDataIdx) in moreValues" :key="'more-' + variantDataIdx"
            class="cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]"
            @click="handleValuePatch(active.idx, variantData)"
            :class="{ 'bg-[#f3f0e9]': variantData.match }"
        >
          <div class="rounded-2xl bg-white flex flex-col items-center p-2"
               :class="{
                 'border-2 border-brand-main': variantData.match,
                 'border-2 border-transparent': !variantData.match
               }"
          >
            <img
                :src="getPresetImageUrl(variantData.idx, 300)"
                :srcset="getPresetImageUrl(variantData.idx, 600)"
                class="max-w-20 w-full h-auto object-contain rounded-2xl"
            >
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-20 mx-auto text-center mt-1">
              {{ variantData.title }}
            </span>
          </div>
        </li>
      </template>
      <!-- "less" trigger - placed as the last entry once "more" is revealed, collapses the list again -->
      <li v-if="showMore"
          class="cursor-pointer flex flex-col items-center px-1 rounded-2xl hover:bg-[#f3f0e9] border-2 border-transparent"
          @click="showMore = false"
      >
        <div class="h-14 w-14 rounded-2xl flex items-center justify-center relative bg-[#f3f0e9]">
          <svg aria-hidden="true" viewBox="0 0 12 12" fill="none" class="size-2.5 shrink-0 rotate-90 text-[#555956]">
            <path d="m1.5 4 4.5 4 4.5-4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
          Weniger
        </span>
      </li>
    </ul>
  </div>
  <item-v3-stones-parameter-loading v-else-if="props.show"/>
</template>

<style scoped>
</style>
